import { ReactNode } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * ParallaxComponent creates a parallax effect for its children components based on the scroll position.
 * It utilizes Framer Motion's `motion.div` to apply the effect.
 *
 * @param {ReactNode} props.children The children components that the parallax effect will be applied to.
 * @param {[number, number]} [props.inputRange=[0, 500]] The scroll range (in pixels) over which the parallax effect occurs. Defaults to [0, 500].
 * @param {[number, number]} [props.outputRange=[0, -100]] The range of movement (in pixels) for the parallax effect. Positive values move the component down, and negative values move it up. Defaults to [0, -100].
 * @param {boolean} [props.disable=false] If true, disables the parallax effect.
 * @returns {ReactNode} A motion.div component wrapping the children, with parallax effect applied based on scroll position.
 */
export const ParallaxComponent = ({
  children,
  inputRange,
  outputRange,
  disable,
  classNames,
}: {
  children: ReactNode;
  inputRange?: [number, number];
  outputRange?: [number, number];
  disable?: boolean;
  classNames?: string;
}) => {
  const { scrollY } = useScroll();
  const y = useTransform(
    scrollY,
    /* This means that the element will go from position (translateY) 0px to -100px, while the scrollY gets from 0px to 500px
    e.g when scrollY equals 250px -halfway through- the position of the element will be -50px.
    When scrollY > 500, the children elements will stop moving and will have a normal scroll behavior.
    */
    inputRange ?? [0, 500],
    outputRange ?? [0, -100],
  );

  return (
    <motion.div
      className={classNames}
      style={{ y: disable ? 0 : y, x: 0, width: '100%' }}
    >
      {children}
    </motion.div>
  );
};
